/**
 * The configuration change log — `GET /configuration/change-log`, newest first.
 *
 * Authority: `apps/api/src/routes/configuration.ts` and migration 009 (`config_change_log`,
 * append-only and hash-chained).
 *
 * Three rules this component exists to keep:
 *
 *  1. **It is a record, not a feed.** There is no control on this panel that writes anything,
 *     because there is no route that writes it: a row appears because a configuration value
 *     moved, in the same statement that moved it, or it does not appear at all. The panel is
 *     read-only in the strongest sense available to a component — it takes rows and renders
 *     them.
 *
 *     **What that lets the foot claim, and what it does not.** A row is written by the
 *     statement that MOVES a value, so the chain evidences movement of values already in
 *     force. It does not evidence the baseline: the seed establishes the whole rule set with
 *     no rows at all, by design. The foot therefore claims the first and says so about the
 *     second, rather than the stronger "a change to the rule set that is not here did not
 *     happen" — which an empty log on a freshly seeded tournament would falsify on sight.
 *
 *  2. **Who is the credential's, never the caller's.** `actorTmsPersonId` comes from the
 *     device credential and no configuration payload carries an actor field. The column is
 *     labelled `Changed By` and prints the TMS person id as stored; where the roster is to
 *     hand a name is shown beside it, and where it is not the id stands alone rather than
 *     being replaced by a guess.
 *
 *  3. **A value that was never set is not a value.** `oldValue` is null on the first write to
 *     a key, and that reads `Not set` — not `0`, not blank, and not the default the engine
 *     happened to be using.
 *
 *  4. **The instant is READ, and the exact instant is KEPT.** The `When` column printed
 *     `2026-09-21T09:02:00.000Z` — the stored `timestamptz`, verbatim. It is exact and it is
 *     unreadable at a glance, on the one column of this table a Technical Delegate scans to
 *     find the change he is looking for. It now prints `Mon 21 Sep, 18:02` in the
 *     TOURNAMENT's zone, with the full instant on the cell's `title` — the house pattern
 *     everywhere a rendering is shorter than the record behind it.
 *
 *     **Through `formatDocumentDateTime` from `@hockeytd/shared`, and through nothing else.**
 *     That helper spells its months and weekdays from FIXED TABLES rather than from `Intl`,
 *     because Node's `en-GB` renders September as `Sept` on some ICU builds and `Sep` on
 *     others — so a locale-formatted date differs between the tournament laptop and the
 *     server for no reason anybody could diagnose. The generated hearing letters already
 *     depend on that guarantee; an audit log has the same claim to it, and a second spelling
 *     of a date in one application is a defect on its own.
 */

import { formatDocumentDateTime } from "@hockeytd/shared";

import type { AppointmentRuleView, ConfigChangeLogRow, OfficialView } from "../../api/types.js";
import { EmptyState } from "../../app/states.js";
import { changeKeyLabel, changeValueLabel } from "./vocabulary.js";

/**
 * The zone used when the tournament's own is not to hand — the same `Asia/Tokyo` the rest of
 * this front end falls back to (`caseText.ts`, `UmTodayPage`). It is a fallback and not a
 * default: `ConfigurationPage` passes the tournament's `timezone` whenever the session has
 * one, and this only stands in for the moment before it settles.
 */
export const FALLBACK_TIMEZONE = "Asia/Tokyo";

export interface ChangeLogPanelProps {
  entries: readonly ConfigChangeLogRow[];
  /** The rules in force, so a logged rule code can be printed as its title. */
  rules: readonly AppointmentRuleView[];
  /** The roster, where it loaded, so an actor id can carry a name. Empty is fine. */
  officials?: readonly OfficialView[];
  /** What the API capped the page at, stated so a full page is not read as the whole log. */
  limit: number;
  /** The tournament's IANA zone. Omitted falls back to {@link FALLBACK_TIMEZONE}. */
  timezone?: string;
}

/**
 * `Mon 21 Sep, 18:02`. An instant that will not parse is printed exactly as it was stored,
 * because a row of an append-only chain must render as *something* — a blank cell in an audit
 * log is worse than an ugly one, and the `title` carries the same string either way.
 */
export function changeLogStamp(at: string, timezone: string): string {
  if (Number.isNaN(new Date(at).getTime())) return at;
  try {
    return formatDocumentDateTime(at, timezone);
  } catch {
    // A zone the runtime does not recognise throws out of `Intl`. The stored instant stands.
    return at;
  }
}

export function ChangeLogPanel({
  entries,
  rules,
  officials = [],
  limit,
  timezone = FALLBACK_TIMEZONE,
}: ChangeLogPanelProps): React.JSX.Element {
  return (
    <section className="panel panel--d2 cfgpanel" aria-labelledby="cfgLogTitle" data-howto="change-log">
      <div className="panel__head">
        <h3 className="panel__title" id="cfgLogTitle">
          Change Log
        </h3>
        <span className="panel__note">Every Configuration Change · Newest First</span>
        <span className="spacer" />
        <span className="btnhint" data-testid="change-log-count">
          {entries.length} of the most recent {limit}
        </span>
      </div>

      {entries.length === 0 ? (
        <EmptyState
          title="No Configuration Change Recorded"
          detail="Nothing in this section has been changed on this installation yet. The log is appended to by the write itself, so an empty log means an untouched configuration and never a lost record."
        />
      ) : (
        <table className="mtable" aria-label="Configuration change log">
          <thead>
            <tr>
              <th scope="col">When</th>
              <th scope="col">What Changed</th>
              <th scope="col">From</th>
              <th scope="col">To</th>
              <th scope="col">Changed By</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.rowHash} data-testid={`change-${entry.rowHash}`}>
                <td className="num" title={entry.at} data-testid={`change-when-${entry.rowHash}`}>
                  {changeLogStamp(entry.at, timezone)}
                </td>
                <td>{changeKeyLabel(entry.key, rules)}</td>
                <td className="num" data-testid={`change-old-${entry.rowHash}`}>
                  {changeValueLabel(entry.oldValue)}
                </td>
                <td className="num" data-testid={`change-new-${entry.rowHash}`}>
                  {changeValueLabel(entry.newValue)}
                </td>
                <td>{actorLabel(entry.actorTmsPersonId, officials)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="panel__foot" data-testid="change-log-foot">
        <b>Append-only.</b> There is no route that edits or removes a row here, and each row is
        chained to the one before it. A change to a value already in force that is not in this
        list did not happen. The starting configuration itself is established when the
        tournament is seeded, without a row — this log evidences what has moved since, not how
        the baseline arrived.
      </div>
    </section>
  );
}

/** The actor as a name where the roster carries one, and as the stored id where it does not. */
function actorLabel(
  actorTmsPersonId: string,
  officials: readonly OfficialView[],
): React.JSX.Element {
  const official = officials.find((entry) => entry.tmsPersonId === actorTmsPersonId);
  return (
    <>
      {official === undefined ? null : <>{official.mediaName} </>}
      <span className="num">{actorTmsPersonId}</span>
    </>
  );
}
